/**
 * Client Code Management Component
 * Admin interface for managing client codes and products.
 * 
 * Features:
 * - Client Codes: Create, edit, activate/deactivate
 * - Client Products: Create, edit, search, filter, activate/deactivate
 * - Tab-based navigation between codes and products
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState, useEffect } from 'react';
import { configToPreset, presetToConfig, CUSTOM_TEXT_MIN, CUSTOM_TEXT_MAX } from '../../lib/mlcTaxHistory';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Settings, Package, Building2, Edit, ToggleLeft, ToggleRight, X, ChevronLeft, ChevronRight, Truck, Upload, Trash2, Image, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAuth } from '../../App';
import { ClientCodeFormModal } from './ClientCodeFormModal';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// BILLING-DNB-TIMEOUT-002 — progress of the background batched exclusion job
const DNB_JOB_LABEL = { queued: 'Queued', running: 'Running', completed: 'Completed', interrupted: 'Interrupted', failed: 'Failed' };
const DnbJobProgress = ({ job }) => {
  const total = job.total || 0;
  const processed = Math.min(job.processed || 0, total);
  const pct = job.status === 'completed' ? 100 : (job.percent ?? (total ? Math.round((processed / total) * 100) : 0));
  const tone = job.status === 'completed' ? 'bg-emerald-500' : job.status === 'failed' ? 'bg-red-500' : job.status === 'interrupted' ? 'bg-amber-500' : 'bg-indigo-600';
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 space-y-3" data-testid="dnb-job-progress">
      <div className="flex items-center justify-between gap-3">
        <span className="text-slate-700">
          Status: <span className="font-semibold text-slate-900" data-testid="dnb-job-status">{DNB_JOB_LABEL[job.status] || job.status}</span>
          {['queued', 'running'].includes(job.status) && <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-indigo-500 animate-pulse align-middle" />}
        </span>
        <span className="text-sm tabular-nums text-slate-700" data-testid="dnb-job-progress-text">{processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} data-testid="dnb-job-progress-bar">
        <div className={`h-full rounded-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[['Excluded', job.excluded || 0, 'text-emerald-700', 'dnb-job-excluded'], ['Skipped', job.skipped || 0, 'text-slate-700', 'dnb-job-skipped'], ['Failed', job.failed_count || 0, (job.failed_count ? 'text-red-700' : 'text-slate-700'), 'dnb-job-failed']].map(([l, v, c, tid]) => (
          <div key={l} className="rounded-md bg-white border border-slate-200 py-1.5">
            <div className={`text-base font-semibold tabular-nums ${c}`} data-testid={tid}>{Number(v).toLocaleString()}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{l}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">
        {job.status === 'completed' ? 'Skipped orders were invoiced, paid or already excluded at write time and were left untouched.'
          : job.error ? job.error
          : `Processed in batches of ${job.batch_size || 500}. Invoiced, Paid and already-excluded orders are never changed; prices are preserved.`}
      </p>
    </div>
  );
};

// Centralized blank-form-state constant — used for new-create AND form
// reset on dialog-close so all fields stay in sync (including the new
// Bill To block, Feb 25, 2026).
const BLANK_FORM_STATE = {
  code: '',
  short_code: '',
  client_name: '',
  description: '',
  mailaway_email: '',
  contact_email: '',
  delivery_email: '',
  tax_delivery_email: '',
  title_delivery_email: '',
  show_fee_summary_in_delivery_email: false,
  delivery_method: 'internal_only',
  // CAT-DM-001 (Aug 2026) — category-level delivery methods (Tax / Title
  // Search resolve independently; legacy delivery_method is the fallback).
  tax_delivery_method: 'internal_only',
  title_delivery_method: 'internal_only',
  // DELIVERY-CONFIG (Sep 2026) — nullable per-category overrides; null = inherit from Tax Search
  mls_delivery_method: null,
  hoa_delivery_method: null,
  utility_delivery_method: null,
  // CLIENT-TYPING-001 (Aug 2026) — client-level Title Search Typing gate.
  title_typing_required: false,
  // BILLING-DNB-001 — client-level Do Not Bill
  do_not_bill: false,
  dnb_reason: '',
  // Feb 28, 2026 — Tax Certificate Delivery Format (per-client).
  // `combined_pdf` keeps the legacy merged-PDF behavior so every
  // existing client is unaffected; flip to `separate_attachments` to
  // deliver cert PDF + editable DOCX + separate supporting docs.
  tax_certificate_delivery_format: 'combined_pdf',
  default_shipping_method: '',
  default_shipping_method_other: '',
  default_shipping_fee: '',
  // MLC-TAXHIST-001
  mlc_tax_history_preset: 'standard',
  mlc_tax_history_custom_text: '',
  use_client_branding: false,
  certificate_display_name: '',
  certificate_disclaimer_entity: '',
  certificate_phone: '',
  certificate_email: '',
  // Customer Bill To
  billing_company_name: '',
  billing_contact_name: '',
  billing_email: '',
  billing_phone: '',
  billing_address_line1: '',
  billing_address_line2: '',
  billing_city: '',
  billing_state: '',
  billing_zip: '',
  billing_ap_email: '',
  billing_invoice_delivery: 'email',
  fax_enabled: true,
  fax_delivery_email: '',
  use_fax_automation: true,
};

/**
 * Client Code Management Component (Admin only)
 */
export const ClientCodeManagement = () => {
  const { user } = useAuth();
  
  // Tabs state
  const [activeTab, setActiveTab] = useState('codes'); // 'codes' or 'products'
  
  // Client Codes state
  const [clientCodes, setClientCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Client Codes list controls (search / filter / sort / pagination)
  const [codeSearch, setCodeSearch] = useState('');
  const [codeStatusFilter, setCodeStatusFilter] = useState('active');
  const [codeSortField, setCodeSortField] = useState('code');
  const [codeSortDir, setCodeSortDir] = useState('asc');
  const [codePage, setCodePage] = useState(1);
  const [codePageSize, setCodePageSize] = useState(25);
  const [showAddDialog, setShowAddDialog] = useState(false);
  // BILLING-DNB-001 — after enabling DNB: { clientId, code, count, orders }
  const [dnbPrompt, setDnbPrompt] = useState(null);
  const [dnbBusy, setDnbBusy] = useState(false);

  // BILLING-DNB-TIMEOUT-002 — poll the background exclusion job while the popup is open
  const dnbJobId = dnbPrompt?.job?.id;
  const dnbJobActive = ['queued', 'running'].includes(dnbPrompt?.job?.status);
  useEffect(() => {
    if (!dnbJobId || !dnbJobActive) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/pricing/billing/dnb-jobs/${dnbJobId}`);
        if (stop) return;
        const job = r.data?.job;
        setDnbPrompt((p) => (p && p.job?.id === dnbJobId ? { ...p, job } : p));
        if (job?.status === 'completed') toast.success(`${(job.excluded || 0).toLocaleString()} order(s) moved to Excluded${job.skipped ? ` · ${job.skipped} skipped` : ''}${job.failed_count ? ` · ${job.failed_count} failed` : ''}`);
        else if (job?.status === 'failed') toast.error(job.error || 'The exclusion job failed.');
        else if (job?.status === 'interrupted') toast.warning('The exclusion job was interrupted. You can resume it.');
      } catch (e) { /* transient — next tick retries */ }
    };
    const t = setInterval(tick, 1500);
    return () => { stop = true; clearInterval(t); };
  }, [dnbJobId, dnbJobActive]);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({ ...BLANK_FORM_STATE });
  const [formErrors, setFormErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Client Products state
  const [clientProducts, setClientProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFormData, setProductFormData] = useState({
    name: '',
    category: '',
    notes: '',
    active: true
  });
  const [productFormErrors, setProductFormErrors] = useState({});
  
  // Client Products filters and pagination
  const [productFilters, setProductFilters] = useState({
    query: '',
    active: 'all'
  });
  const [productPage, setProductPage] = useState(1);
  const [productPageSize] = useState(25);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchDebounceTimeout, setSearchDebounceTimeout] = useState(null);
  const [abortController, setAbortController] = useState(null);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null, // 'code' or 'product'
    item: null,
    action: null // 'activate' or 'deactivate'
  });
  
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchClientCodes();
      fetchClientProducts();
    }
  }, [user]);
  
  // ==================== CLIENT CODES FUNCTIONS ====================
  
  const fetchClientCodes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/client-codes`);
      setClientCodes(response.data);
    } catch (error) {
      console.error('Error fetching client codes:', error);
      toast.error('Failed to load client codes');
    } finally {
      setIsLoading(false);
    }
  };
  
  const generateShortCode = (fullCode) => {
    const cleaned = fullCode.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    return cleaned.substring(0, 4).padEnd(4, '0');
  };
  
  const handleFieldChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // When fax_enabled is unchecked, reset auto-send to false
      if (field === 'fax_enabled' && !value) {
        next.use_fax_automation = false;
      }
      return next;
    });
    
    if (field === 'code' && !editingCode) {
      const suggested = generateShortCode(value);
      setFormData(prev => ({ ...prev, short_code: suggested }));
    }
    
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!formData.code.trim()) {
      errors.code = 'Full Client Code is required';
    }
    
    if (!formData.short_code.trim()) {
      errors.short_code = 'Short Code is required';
    } else if (!/^[A-Z0-9]{2,4}$/.test(formData.short_code)) {
      errors.short_code = 'Short Code must be 2-4 uppercase letters/numbers';
    }
    
    if (!formData.client_name.trim()) {
      errors.client_name = 'Client Name is required';
    }
    
    // Mail-Away Email - MANDATORY with format validation
    if (!formData.mailaway_email?.trim()) {
      errors.mailaway_email = 'Mail-Away Email is required';
    } else if (!emailRegex.test(formData.mailaway_email)) {
      errors.mailaway_email = 'Invalid email format';
    }
    
    // Contact Email - optional but validate format if provided
    if (formData.contact_email && !emailRegex.test(formData.contact_email)) {
      errors.contact_email = 'Invalid email format';
    }
    
    // Delivery Email - optional but validate format if provided
    if (formData.tax_delivery_email && !emailRegex.test(formData.tax_delivery_email)) {
      errors.tax_delivery_email = 'Invalid email format';
    }
    if (formData.title_delivery_email && !emailRegex.test(formData.title_delivery_email)) {
      errors.title_delivery_email = 'Invalid email format';
    }
    if (formData.delivery_email && !emailRegex.test(formData.delivery_email)) {
      errors.delivery_email = 'Invalid email format';
    }
    
    // Delivery Methods - MANDATORY (CAT-DM-001: per workflow category)
    if (!formData.tax_delivery_method?.trim()) {
      errors.tax_delivery_method = 'Tax Product Delivery Method is required';
    }
    if (!formData.title_delivery_method?.trim()) {
      errors.title_delivery_method = 'Title Search Product Delivery Method is required';
    }
    
    // Default Shipping Method - MANDATORY (cannot be empty or 'none')
    if (!formData.default_shipping_method?.trim() || formData.default_shipping_method === 'none') {
      errors.default_shipping_method = 'Default Shipping Method is required';
    }
    
    // Validate "Other" shipping method requires specification
    if (formData.default_shipping_method === 'Other' && !formData.default_shipping_method_other?.trim()) {
      errors.default_shipping_method_other = 'Please specify the shipping method when "Other" is selected';
    }
    
    // Default Shipping Fee - MANDATORY with numeric validation
    if (!formData.default_shipping_fee && formData.default_shipping_fee !== 0) {
      errors.default_shipping_fee = 'Default Shipping Fee is required';
    } else if (isNaN(parseFloat(formData.default_shipping_fee)) || parseFloat(formData.default_shipping_fee) < 0) {
      errors.default_shipping_fee = 'Please enter a valid shipping fee (0 or greater)';
    }
    
    // MLC-TAXHIST-001 — custom wording must be real plain text
    if (formData.mlc_tax_history_preset === 'custom') {
      const t = (formData.mlc_tax_history_custom_text || '').trim();
      if (t.length < CUSTOM_TEXT_MIN || t.length > CUSTOM_TEXT_MAX) {
        errors.mlc_tax_history_custom_text = `Custom wording must be ${CUSTOM_TEXT_MIN}–${CUSTOM_TEXT_MAX} characters`;
      } else if (/[<>]/.test(t)) {
        errors.mlc_tax_history_custom_text = 'Custom wording cannot contain < or >';
      }
    }

    // Fax Automation — if fax is enabled, auto-send must be explicitly set
    if (formData.fax_enabled && formData.use_fax_automation === null) {
      errors.use_fax_automation = 'Please configure Auto-send Fax setting when Fax is enabled for this client.';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      // Prepare data with proper type conversion for shipping fee
      const submitData = {
        ...formData,
        short_code: formData.short_code.toUpperCase(),
        // CAT-DM-001 — keep the legacy field coherent: it follows the Tax
        // category (legacy consumers + uses_external_tax_portal are tax-side)
        delivery_method: formData.tax_delivery_method || formData.delivery_method || 'internal_only',
        // DELIVERY-CONFIG — send explicit null for "Inherit from Tax Search"
        mls_delivery_method: formData.mls_delivery_method || null,
        hoa_delivery_method: formData.hoa_delivery_method || null,
        utility_delivery_method: formData.utility_delivery_method || null,
        // CLIENT-DELIVERY-EMAIL-ROUTING-001 — empty = inherit legacy Completion Email
        tax_delivery_email: (formData.tax_delivery_email || '').trim() || null,
        title_delivery_email: (formData.title_delivery_email || '').trim() || null,
        // Convert shipping fee to float or null
        default_shipping_fee: formData.default_shipping_fee ? parseFloat(formData.default_shipping_fee) : null,
        // Clear "Other" text if not using "Other" method
        default_shipping_method_other: formData.default_shipping_method === 'Other' 
          ? formData.default_shipping_method_other 
          : null,
        // Ensure fax fields are always boolean, never null
        fax_enabled: !!formData.fax_enabled,
        use_fax_automation: !!formData.use_fax_automation,
        // MLC-TAXHIST-001 — structured config; form-only helper keys are dropped
        mlc_tax_history: presetToConfig(formData.mlc_tax_history_preset, formData.mlc_tax_history_custom_text),
      };
      delete submitData.mlc_tax_history_preset;
      delete submitData.mlc_tax_history_custom_text;
      
      const dnbJustEnabled = !!editingCode && !!submitData.do_not_bill && !editingCode.do_not_bill;
      if (editingCode) {
        await axios.put(`${API}/client-codes/${editingCode.id}`, submitData);
        if (dnbJustEnabled) {
          // BILLING-DNB-001 — ask whether existing Unbilled orders should be excluded too (explicit confirmation only)
          try {
            const el = await axios.get(`${API}/pricing/billing/clients/${editingCode.id}/dnb-eligible`);
            setDnbPrompt({ clientId: editingCode.id, code: submitData.code || editingCode.code, count: el.data.count, orders: el.data.orders || [], job: el.data.active_job || null });
          } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load existing Unbilled orders for this client.'); }
        }
        // Upload logo if one was selected
        if (logoFile) {
          const logoFormData = new FormData();
          logoFormData.append('file', logoFile);
          await axios.post(`${API}/client-codes/${editingCode.id}/logo`, logoFormData);
        }
        toast.success('Client code updated successfully');
      } else {
        const resp = await axios.post(`${API}/client-codes`, submitData);
        // Upload logo for newly created client code
        if (logoFile && resp.data?.id) {
          const logoFormData = new FormData();
          logoFormData.append('file', logoFile);
          await axios.post(`${API}/client-codes/${resp.data.id}/logo`, logoFormData);
        }
        toast.success('Client code created successfully');
      }
      
      setShowAddDialog(false);
      setEditingCode(null);
      setFormData({ ...BLANK_FORM_STATE });
      setFormErrors({});
      setLogoFile(null);
      setLogoPreview(null);
      setExistingLogoUrl(null);
      fetchClientCodes();
    } catch (error) {
      console.error('Error saving client code:', error);
      // Display backend error message if available
      const errorDetail = error.response?.data?.detail;
      if (errorDetail) {
        toast.error(errorDetail);
        // Set form-specific errors based on the message
        const lower = errorDetail.toLowerCase();
        if (lower.startsWith('full code')) {
          setFormErrors({ code: errorDetail });
        } else if (lower.startsWith('short code')) {
          setFormErrors({ short_code: errorDetail });
        } else if (lower.includes('short code')) {
          setFormErrors({ short_code: errorDetail });
        } else if (lower.includes('client code') || lower.includes('full code')) {
          setFormErrors({ code: errorDetail });
        }
      } else {
        toast.error('Failed to save client code');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      ...BLANK_FORM_STATE,
      code: code.code,
      short_code: code.short_code,
      client_name: code.client_name,
      description: code.description || '',
      mailaway_email: code.mailaway_email || '',
      contact_email: code.contact_email || '',
      delivery_email: code.delivery_email || '',
      tax_delivery_email: code.tax_delivery_email || '',
      title_delivery_email: code.title_delivery_email || '',
      show_fee_summary_in_delivery_email: code.show_fee_summary_in_delivery_email || false,
      uses_external_tax_portal: (code.tax_delivery_method || code.delivery_method) === 'external_portal',
      delivery_method: code.delivery_method || 'internal_only',
      // CAT-DM-001 — show the effective value (inherit legacy when the
      // category field was never saved)
      tax_delivery_method: code.tax_delivery_method || code.delivery_method || 'internal_only',
      title_delivery_method: code.title_delivery_method || code.delivery_method || 'internal_only',
      // DELIVERY-CONFIG — keep null (= inherit) unless explicitly configured
      mls_delivery_method: code.mls_delivery_method || null,
      hoa_delivery_method: code.hoa_delivery_method || null,
      utility_delivery_method: code.utility_delivery_method || null,
      title_typing_required: !!code.title_typing_required,
      do_not_bill: !!code.do_not_bill,
      dnb_reason: code.dnb_reason || '',
      dnb_history: code.dnb_history || [],
      tax_certificate_delivery_format: code.tax_certificate_delivery_format || 'combined_pdf',
      default_shipping_method: code.default_shipping_method || '',
      default_shipping_method_other: code.default_shipping_method_other || '',
      default_shipping_fee: code.default_shipping_fee != null ? code.default_shipping_fee.toString() : '',
      mlc_tax_history_preset: configToPreset(code.mlc_tax_history),
      mlc_tax_history_custom_text: code.mlc_tax_history?.custom_text || '',
      use_client_branding: code.use_client_branding || false,
      certificate_display_name: code.certificate_display_name || '',
      certificate_disclaimer_entity: code.certificate_disclaimer_entity || '',
      certificate_phone: code.certificate_phone || '',
      certificate_email: code.certificate_email || '',
      // Customer Bill To (Feb 25, 2026)
      billing_company_name: code.billing_company_name || '',
      billing_contact_name: code.billing_contact_name || '',
      billing_email: code.billing_email || '',
      billing_phone: code.billing_phone || '',
      billing_address_line1: code.billing_address_line1 || '',
      billing_address_line2: code.billing_address_line2 || '',
      billing_city: code.billing_city || '',
      billing_state: code.billing_state || '',
      billing_zip: code.billing_zip || '',
      billing_ap_email: code.billing_ap_email || '',
      billing_invoice_delivery: code.billing_invoice_delivery || 'email',
      fax_enabled: code.fax_enabled || false,
      fax_delivery_email: code.fax_delivery_email || '',
      use_fax_automation: code.use_fax_automation || false,
    });
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(code.certificate_logo_url || null);
    setShowAddDialog(true);
  };
  
  const handleCloseDialog = () => {
    setShowAddDialog(false);
    setEditingCode(null);
    setFormData({ ...BLANK_FORM_STATE });
    setFormErrors({});
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(null);
  };
  
  const handleToggleClientCodeActive = async (code) => {
    const action = code.active ? 'deactivate' : 'activate';
    // Show confirmation dialog instead of native confirm
    setConfirmDialog({
      open: true,
      type: 'code',
      item: code,
      action: action
    });
  };
  
  const confirmToggleClientCode = async () => {
    const code = confirmDialog.item;
    const action = confirmDialog.action;
    
    setConfirmDialog({ open: false, type: null, item: null, action: null });
    
    try {
      await axios.put(`${API}/client-codes/${code.id}`, {
        active: !code.active
      });
      
      toast.success(`Client code ${code.active ? 'deactivated' : 'activated'} successfully`);
      fetchClientCodes();
    } catch (error) {
      console.error(`Error ${action}ing client code:`, error);
      toast.error(`Failed to ${action} client code`);
    }
  };
  
  const handleNewClientCode = () => {
    setShowAddDialog(true);
  };

  // ==================== CLIENT PRODUCTS FUNCTIONS ====================
  
  const fetchClientProducts = async (resetPage = false) => {
    if (abortController) {
      abortController.abort();
    }
    
    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    
    setIsLoadingProducts(true);
    try {
      const params = new URLSearchParams();
      
      if (productFilters.query.trim()) {
        params.append('query', productFilters.query.trim());
      }
      
      if (productFilters.active !== 'all') {
        params.append('active', productFilters.active);
      }
      
      const currentPage = resetPage ? 1 : productPage;
      params.append('page', currentPage.toString());
      params.append('page_size', productPageSize.toString());
      
      const response = await axios.get(`${API}/client-products?${params.toString()}`, {
        signal: newAbortController.signal
      });
      
      if (response.data.items) {
        setClientProducts(response.data.items);
        setTotalProducts(response.data.total || 0);
      } else if (Array.isArray(response.data)) {
        setClientProducts(response.data);
        setTotalProducts(response.data.length);
      }
      
      if (resetPage) {
        setProductPage(1);
      }
    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error('Error fetching client products:', error);
        toast.error('Failed to load client products');
      }
    } finally {
      setIsLoadingProducts(false);
      setAbortController(null);
    }
  };
  
  const handleProductFilterChange = (field, value) => {
    setProductFilters(prev => ({ ...prev, [field]: value }));
    
    if (field === 'query') {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout);
      }
      
      const timeout = setTimeout(() => {
        fetchClientProducts(true);
      }, 300);
      
      setSearchDebounceTimeout(timeout);
    } else {
      fetchClientProducts(true);
    }
  };
  
  const clearProductFilters = () => {
    setProductFilters({
      query: '',
      active: 'all'
    });
    setProductPage(1);
    fetchClientProducts();
  };
  
  const handleProductFieldChange = (field, value) => {
    setProductFormData(prev => ({ ...prev, [field]: value }));
    if (productFormErrors[field]) {
      setProductFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  const validateProductForm = () => {
    const errors = {};
    
    if (!productFormData.name.trim()) {
      errors.name = 'Product Name is required';
    } else {
      const trimmed = productFormData.name.trim().replace(/\s+/g, ' ');
      if (trimmed.length < 2 || trimmed.length > 30) {
        errors.name = 'Product Name must be between 2 and 30 characters';
      }
    }
    
    setProductFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleProductSubmit = async () => {
    if (!validateProductForm()) return;
    
    setIsLoadingProducts(true);
    try {
      const submitData = {
        ...productFormData,
        name: productFormData.name.trim().replace(/\s+/g, ' ')
      };
      
      if (editingProduct) {
        await axios.put(`${API}/client-products/${editingProduct.id}`, submitData);
        toast.success(`Updated '${submitData.name}'`);
      } else {
        await axios.post(`${API}/client-products`, submitData);
        toast.success(`Product '${submitData.name}' created successfully`);
      }
      
      setShowAddProductDialog(false);
      setEditingProduct(null);
      setProductFormData({ name: '', category: '', notes: '', active: true });
      setProductFormErrors({});
      fetchClientProducts();
    } catch (error) {
      console.error('Error saving client product:', error);
      
      if (error.response?.status === 400 || error.response?.status === 409 || error.response?.status === 422) {
        const errorMsg = error.response.data.detail || 'Failed to save product';
        toast.error(errorMsg);
        
        if (errorMsg.toLowerCase().includes('already exists')) {
          setProductFormErrors({ name: errorMsg });
        } else if (errorMsg.toLowerCase().includes('product name')) {
          setProductFormErrors({ name: errorMsg });
        }
      } else {
        toast.error('Failed to save client product');
      }
    } finally {
      setIsLoadingProducts(false);
    }
  };
  
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      name: product.name,
      category: product.category || '',
      notes: product.notes || '',
      active: product.active ?? true
    });
    setShowAddProductDialog(true);
  };
  
  const handleCloseProductDialog = () => {
    setShowAddProductDialog(false);
    setEditingProduct(null);
    setProductFormData({ name: '', category: '', notes: '', active: true });
    setProductFormErrors({});
  };
  
  const handleToggleProductActive = async (product) => {
    const action = product.active ? 'deactivate' : 'activate';
    // Show confirmation dialog instead of native confirm
    setConfirmDialog({
      open: true,
      type: 'product',
      item: product,
      action: action
    });
  };
  
  const confirmToggleProduct = async () => {
    const product = confirmDialog.item;
    const action = confirmDialog.action;
    
    setConfirmDialog({ open: false, type: null, item: null, action: null });
    
    try {
      await axios.put(`${API}/client-products/${product.id}`, {
        active: !product.active
      });
      
      toast.success(`Product ${product.active ? 'deactivated' : 'activated'} successfully`);
      fetchClientProducts();
    } catch (error) {
      console.error(`Error ${action}ing product:`, error);
      toast.error(`Failed to ${action} product`);
    }
  };

  // ==================== CLIENT CODES LIST CONTROLS ====================

  useEffect(() => {
    setCodePage(1);
  }, [codeSearch, codeStatusFilter, codePageSize]);

  const handleCodeSort = (field) => {
    if (codeSortField === field) {
      setCodeSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setCodeSortField(field);
      setCodeSortDir('asc');
    }
  };

  const filteredCodes = React.useMemo(() => {
    let list = [...clientCodes];
    if (codeStatusFilter === 'active') list = list.filter((c) => c.active);
    else if (codeStatusFilter === 'inactive') list = list.filter((c) => !c.active);

    const q = codeSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        (c.code || '').toLowerCase().includes(q) ||
        (c.short_code || '').toLowerCase().includes(q) ||
        (c.client_name || '').toLowerCase().includes(q) ||
        (c.delivery_method || '').replace(/_/g, ' ').toLowerCase().includes(q)
      );
    }

    const dir = codeSortDir === 'asc' ? 1 : -1;
    const getVal = (c) => {
      switch (codeSortField) {
        case 'code': return (c.code || '').toLowerCase();
        case 'short_code': return (c.short_code || '').toLowerCase();
        case 'client_name': return (c.client_name || '').toLowerCase();
        case 'delivery': return (c.delivery_method || '').replace(/_/g, ' ').toLowerCase();
        case 'status': return c.active ? 0 : 1;
        default: return '';
      }
    };
    list.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return list;
  }, [clientCodes, codeSearch, codeStatusFilter, codeSortField, codeSortDir]);

  const totalFilteredCodes = filteredCodes.length;
  const codeTotalPages = Math.max(1, Math.ceil(totalFilteredCodes / codePageSize));
  const effectiveCodePage = Math.min(codePage, codeTotalPages);
  const codeRangeStart = totalFilteredCodes === 0 ? 0 : (effectiveCodePage - 1) * codePageSize + 1;
  const codeRangeEnd = Math.min(effectiveCodePage * codePageSize, totalFilteredCodes);
  const paginatedCodes = filteredCodes.slice((effectiveCodePage - 1) * codePageSize, effectiveCodePage * codePageSize);

  const codePageNumbers = (() => {
    const maxBtns = 5;
    let start = Math.max(1, effectiveCodePage - 2);
    const end = Math.min(codeTotalPages, start + maxBtns - 1);
    start = Math.max(1, end - maxBtns + 1);
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  })();

  const renderSortIcon = (field) => {
    if (codeSortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    return codeSortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const SortableTh = ({ field, label }) => (
    <th
      className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none hover:!bg-blue-700 transition-colors"
      onClick={() => handleCodeSort(field)}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {renderSortIcon(field)}
      </span>
    </th>
  );

  // Access control
  if (user?.role !== 'admin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Only Admin users can manage client codes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const totalPages = Math.ceil(totalProducts / productPageSize);
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="client-code-management">
      <Card>
        <CardHeader className="pb-4 border-b border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-blue-600" />
              <div>
                <CardTitle>Client Codes</CardTitle>
                <CardDescription>Manage client codes, delivery settings and billing details</CardDescription>
              </div>
            </div>
            <Button onClick={handleNewClientCode} data-testid="add-client-code-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Client Code
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="space-y-4">
            {/* Toolbar: search + status filter + page size */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={codeSearch}
                  onChange={(e) => setCodeSearch(e.target.value)}
                  placeholder="Search code, short code, client name, delivery..."
                  className="pl-9 pr-8 h-9"
                  data-testid="client-code-search-input"
                />
                {codeSearch && (
                  <button
                    type="button"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setCodeSearch('')}
                    data-testid="client-code-search-clear"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center rounded-md border border-gray-200 overflow-hidden" data-testid="client-code-status-filter">
                {[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'all', label: 'All' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCodeStatusFilter(opt.value)}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      codeStatusFilter === opt.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`status-filter-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-500 whitespace-nowrap">Rows per page</span>
                <Select value={String(codePageSize)} onValueChange={(v) => setCodePageSize(Number(v))}>
                  <SelectTrigger className="h-9 w-[76px]" data-testid="client-code-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading client codes...</div>
            ) : totalFilteredCodes === 0 ? (
              <div className="text-center py-10 text-gray-500 border rounded-lg" data-testid="client-codes-empty">
                {codeSearch.trim() || codeStatusFilter !== 'all'
                  ? 'No client codes match your search or filter.'
                  : 'No client codes found. Click "Add Client Code" to create one.'}
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="tf-thead">
                    <tr>
                      <SortableTh field="code" label="Code" />
                      <SortableTh field="short_code" label="Short" />
                      <SortableTh field="client_name" label="Client Name" />
                      <SortableTh field="delivery" label="Delivery" />
                      <th className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Typing</th>
                      <SortableTh field="status" label="Status" />
                      <th className="!bg-blue-600 !text-white px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedCodes.map((code) => (
                      <tr key={code.id} className={!code.active ? 'bg-gray-50 opacity-60' : 'hover:bg-blue-50/40 transition-colors'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{code.code}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <Badge variant="outline">{code.short_code}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{code.client_name}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          <span className="capitalize" data-testid={`delivery-cell-${code.code}`}>
                            Tax: {((code.tax_delivery_method || code.delivery_method || 'internal_only').replace('_', ' '))}
                            <span className="mx-1 text-gray-300">·</span>
                            Title: {((code.title_delivery_method || code.delivery_method || 'internal_only').replace('_', ' '))}
                            {/* DELIVERY-CONFIG: only surface explicit per-category overrides */}
                            {[['MLS', code.mls_delivery_method], ['HOA', code.hoa_delivery_method], ['Utility', code.utility_delivery_method]]
                              .filter(([, v]) => v)
                              .map(([lbl, v]) => (
                                <React.Fragment key={lbl}>
                                  <span className="mx-1 text-gray-300">·</span>
                                  {lbl}: {v.replace('_', ' ')}
                                </React.Fragment>
                              ))}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs" data-testid={`typing-cell-${code.code}`}>
                          <Badge variant="outline" className={code.title_typing_required ? 'border-blue-300 text-blue-700 bg-blue-50' : 'text-gray-500'}>
                            {code.title_typing_required ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={code.active ? 'default' : 'secondary'}>
                            {code.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(code)} data-testid={`edit-code-${code.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleClientCodeActive(code)}
                              title={code.active ? 'Deactivate' : 'Activate'}
                              data-testid={`toggle-code-${code.id}`}
                            >
                              {code.active ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination footer */}
            {!isLoading && totalFilteredCodes > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-600" data-testid="client-code-range-text">
                  Showing {codeRangeStart}&ndash;{codeRangeEnd} of {totalFilteredCodes} client code{totalFilteredCodes === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2.5"
                    disabled={effectiveCodePage <= 1}
                    onClick={() => setCodePage(effectiveCodePage - 1)}
                    data-testid="client-code-prev-page"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>
                  {codePageNumbers.map((p) => (
                    <Button
                      key={p}
                      variant={p === effectiveCodePage ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setCodePage(p)}
                      data-testid={`client-code-page-${p}`}
                    >
                      {p}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2.5"
                    disabled={effectiveCodePage >= codeTotalPages}
                    onClick={() => setCodePage(effectiveCodePage + 1)}
                    data-testid="client-code-next-page"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* BILLING-DNB-001 / BILLING-DNB-TIMEOUT-002 — existing Unbilled orders prompt; exclusion runs as a background batched job */}
      <AlertDialog open={!!dnbPrompt} onOpenChange={(v) => { if (!v && !dnbBusy) setDnbPrompt(null); }}>
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-xl min-w-0 overflow-hidden gap-5 p-6" data-testid="dnb-existing-dialog">
          <AlertDialogHeader className="min-w-0 space-y-3">
            <AlertDialogTitle className="text-lg leading-snug break-words pr-2">
              {dnbPrompt?.job ? (dnbPrompt.job.status === 'completed' ? 'Existing Unbilled orders excluded' : 'Excluding existing Unbilled orders…') : 'Exclude existing Unbilled orders from billing?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="min-w-0 space-y-3 text-sm text-slate-600">
                <p className="break-words">Do Not Bill is now enabled for <span className="font-semibold text-slate-800">{dnbPrompt?.code}</span>. New orders will be excluded automatically.</p>
                {!dnbPrompt?.job ? (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3" data-testid="dnb-eligible-count">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-slate-700">Eligible existing <strong>Unbilled</strong> orders</span>
                      <span className="text-xl font-semibold tabular-nums text-slate-900" data-testid="dnb-eligible-count-value">{(dnbPrompt?.count ?? 0).toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">Invoiced, Paid and already-excluded orders are never changed. Prices and order details are preserved. Runs in the background in batches of 500 — you can close this window at any time.</p>
                  </div>
                ) : <DnbJobProgress job={dnbPrompt.job} />}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:space-x-0">
            {!dnbPrompt?.job ? (
              <>
                <AlertDialogCancel className="w-full m-0 whitespace-nowrap" disabled={dnbBusy} onClick={() => { setDnbPrompt(null); toast.success('DNB applies to future orders only.'); }} data-testid="dnb-future-only-btn">No, Future Orders Only</AlertDialogCancel>
                <AlertDialogAction className="w-full m-0 whitespace-nowrap" disabled={dnbBusy || !dnbPrompt?.count} data-testid="dnb-exclude-existing-btn"
                  onClick={async (e) => {
                    e.preventDefault();
                    setDnbBusy(true);
                    try {
                      const r = await axios.post(`${API}/pricing/billing/clients/${dnbPrompt.clientId}/dnb-exclude-existing`, {});
                      setDnbPrompt((p) => ({ ...p, job: r.data?.job }));
                    } catch (err) {
                      const d = err?.response?.data?.detail;
                      if (err?.response?.status === 409 && d?.job) { setDnbPrompt((p) => ({ ...p, job: d.job })); toast.info('An exclusion job is already running for this client.'); }
                      else toast.error((typeof d === 'string' && d) || 'Failed to start the exclusion job.');
                    }
                    finally { setDnbBusy(false); }
                  }}>{dnbBusy ? 'Starting…' : `Yes, Exclude ${(dnbPrompt?.count ?? 0).toLocaleString()} Orders`}</AlertDialogAction>
              </>
            ) : (
              <>
                {['interrupted', 'failed'].includes(dnbPrompt.job.status) ? (
                  <Button variant="outline" className="w-full m-0 whitespace-nowrap" disabled={dnbBusy} data-testid="dnb-job-resume-btn"
                    onClick={async () => {
                      setDnbBusy(true);
                      try { const r = await axios.post(`${API}/pricing/billing/dnb-jobs/${dnbPrompt.job.id}/resume`); setDnbPrompt((p) => ({ ...p, job: r.data?.job })); }
                      catch (err) { toast.error(err?.response?.data?.detail || 'Could not resume the job.'); }
                      finally { setDnbBusy(false); }
                    }}>Resume</Button>
                ) : <span className="hidden sm:block" />}
                <AlertDialogCancel className="w-full m-0 whitespace-nowrap" onClick={() => setDnbPrompt(null)} data-testid="dnb-job-close-btn">
                  {dnbPrompt.job.status === 'completed' ? 'Done' : 'Run in Background'}
                </AlertDialogCancel>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add/Edit Client Code Dialog */}
      <ClientCodeFormModal
        open={showAddDialog}
        onClose={handleCloseDialog}
        editingCode={editingCode}
        formData={formData}
        formErrors={formErrors}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        logoPreview={logoPreview}
        existingLogoUrl={existingLogoUrl}
        setLogoFile={setLogoFile}
        setLogoPreview={setLogoPreview}
        setExistingLogoUrl={setExistingLogoUrl}
        API={API}
      />

      {/* Add/Edit Client Product Dialog */}
      <Dialog open={showAddProductDialog} onOpenChange={handleCloseProductDialog}>
        <DialogContent 
          className="sm:max-w-[500px]"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleProductSubmit();
            } else if (e.key === 'Escape') {
              handleCloseProductDialog();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Client Product' : 'Add Client Product'}</DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Update the product details below' : 'Enter the details for the new client product (global - can be used by any client)'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product Name <span className="text-red-500">*</span></Label>
              <Input
                autoFocus
                value={productFormData.name}
                onChange={(e) => handleProductFieldChange('name', e.target.value)}
                placeholder="e.g., Title Search (2-30 characters)"
                className={productFormErrors.name ? 'border-red-400' : ''}
                maxLength={30}
                data-testid="input-product-name"
              />
              {productFormErrors.name && (
                <p className="text-red-600 text-xs mt-1">{productFormErrors.name}</p>
              )}
              <p className="text-xs text-gray-500">
                {productFormData.name.trim().length}/30 characters
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Category (Optional)</Label>
              <Input
                value={productFormData.category}
                onChange={(e) => handleProductFieldChange('category', e.target.value)}
                placeholder="e.g., Search Services"
                data-testid="input-product-category"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={productFormData.notes}
                onChange={(e) => handleProductFieldChange('notes', e.target.value)}
                placeholder="Additional notes or description"
                rows={3}
                data-testid="input-product-notes"
              />
            </div>
            
            <div className="flex items-center space-x-3 pt-2">
              <input
                type="checkbox"
                id="product-active"
                checked={productFormData.active}
                onChange={(e) => handleProductFieldChange('active', e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                data-testid="checkbox-product-active"
              />
              <Label htmlFor="product-active" className="cursor-pointer">
                Active (visible in order forms)
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseProductDialog} data-testid="cancel-product-dialog">
              Cancel
            </Button>
            <Button onClick={handleProductSubmit} disabled={isLoadingProducts} data-testid="submit-product">
              {isLoadingProducts ? 'Saving...' : editingProduct ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Activate/Deactivate Actions */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, item: null, action: null })}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === 'deactivate' ? 'Deactivate' : 'Activate'} {confirmDialog.type === 'code' ? 'Client Code' : 'Product'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'deactivate' ? (
                <>
                  Are you sure you want to deactivate{' '}
                  <span className="font-semibold text-gray-900">
                    {confirmDialog.type === 'code' ? confirmDialog.item?.code : confirmDialog.item?.name}
                  </span>
                  ? {confirmDialog.type === 'code' 
                    ? 'This client code will no longer be available for new orders.' 
                    : 'This product will no longer appear in order forms.'}
                </>
              ) : (
                <>
                  Are you sure you want to activate{' '}
                  <span className="font-semibold text-gray-900">
                    {confirmDialog.type === 'code' ? confirmDialog.item?.code : confirmDialog.item?.name}
                  </span>
                  ? {confirmDialog.type === 'code' 
                    ? 'This client code will become available for new orders.' 
                    : 'This product will appear in order forms.'}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-dialog-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog.type === 'code' ? confirmToggleClientCode : confirmToggleProduct}
              className={confirmDialog.action === 'deactivate' ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'}
              data-testid="confirm-dialog-action"
            >
              {confirmDialog.action === 'deactivate' ? 'Deactivate' : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientCodeManagement;
